import { Spinner } from "@/components/icons/Spinner";
import { Link } from "@/i18n/Link";
import { type VariantProps, classed, clsx } from "@frend-digital/ui";
import type { ComponentProps } from "react";
import styles from "./Button.module.css";
import { NavigationMenuPrimitiveLink, useNavigationMenuContext } from "../NavigationMenu";

const ButtonBase = classed.button(clsx(styles.root, "button"), {
	variants: {
		loading: {
			true: styles.loading,
		},
		color: {
			red: styles.red,
			black: styles.black,
			white: styles.white,
			beige: styles.beige,
			outlined: styles.outlined,
			outlinedTransparent: styles.outlinedTransparent,
		},
		isDisabled: {
			true: styles.disabled,
		},
		size: {
			full: styles.full,
			caption: styles.sizeCaption,
		},
	},
	defaultVariants: {
		color: "black",
	},
});

const ButtonLink = classed(Link, ButtonBase, "button");

export interface ButtonProps extends ComponentProps<typeof ButtonBase> {
	loading?: boolean;
	href?: string;
	prefetch?: boolean;
	asChild?: boolean;
	scroll?: boolean;
	target?: ComponentProps<typeof Link>["target"];
	rel?: ComponentProps<typeof Link>["rel"];
}

const Button = ({
	loading,
	children,
	className,
	href,
	asChild,
	disabled,
	target,
	rel,
	...rest
}: ButtonProps) => {
	const inner = (
		<>
			<span className={styles.inner}>{children}</span>
			{loading && (
				<span className={styles.loadingSpinner}>
					<Spinner />
				</span>
			)}
		</>
	);

	if (asChild) {
		return (
			<Button
				loading={loading}
				asChild={true}
				className={clsx(className, disabled && styles.disabled)}
				href={href}
				target={target}
				rel={rel}
				{...rest}
			>
				{children}
			</Button>
		);
	}

	if (href) {
		const isPdf = href.endsWith(".pdf");
		return (
			<ButtonLink
				className={clsx(className, disabled && styles.disabled)}
				{...(rest as ComponentProps<typeof Link> & VariantProps<typeof ButtonBase>)}
				href={href}
				target={target ?? (isPdf ? "_blank" : undefined)}
				rel={rel ?? (isPdf ? "noopener noreferrer" : undefined)}
			>
				{inner}
			</ButtonLink>
		);
	}

	return (
		<ButtonBase
			className={clsx(className, disabled && styles.disabled)}
			loading={loading}
			disabled={loading || disabled}
			{...rest}
		>
			{inner}
		</ButtonBase>
	);
};

const WrappedButton: typeof Button = (props) => {
	const isNavigationMenu = useNavigationMenuContext();
	if (isNavigationMenu) {
		return (
			<NavigationMenuPrimitiveLink asChild>
				<Button {...props} />
			</NavigationMenuPrimitiveLink>
		);
	}
	return <Button {...props} />;
};

export { WrappedButton as Button };
